import { uploadAttachment, getAttachmentsByEntity, downloadAttachment, downloadThumbnail, deleteAttachment } from '../../services/api';

export function formatDescriptionHtml(text) {
  if (!text) return '';
  // If content has list tags, pass through as-is (just clean up &nbsp;)
  if (/<(ul|ol)\b/i.test(text)) {
    return text.replace(/&nbsp;/g, ' ');
  }
  // If no HTML tags, convert plain text newlines to <br>
  if (!/<(p|h[1-6]|div|br|blockquote)\b/i.test(text)) {
    return text.replace(/\n/g, '<br>');
  }
  // Clean up broken contentEditable output where each word is a separate <p> tag
  // Split into <p> segments, merge short ones (< 40 chars stripped) into a single paragraph
  // IMPORTANT: stripHtml is ONLY used to measure length/emptiness for merge decisions.
  // We must push the ORIGINAL segment HTML (preserving <strong>/<b>/<em>/<u> etc.) into merged[],
  // otherwise inline formatting like bold typed in the Quill editor will be lost.
  const segments = text.split(/<\/p>\s*<p>/i);
  if (segments.length <= 1) return text;
  const stripHtml = (s) => s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  const merged = [];
  let buffer = '';
  for (let i = 0; i < segments.length; i++) {
    const original = segments[i];              // keep original HTML (with inline tags)
    const clean = stripHtml(original);         // only for length/emptiness checks
    if (clean === '') {
      // Empty <p> = intentional paragraph break
      if (buffer) { merged.push(buffer); buffer = ''; }
      merged.push('');
    } else if (clean.length < 40 && i > 0 && i < segments.length - 1) {
      // Short segment - accumulate into buffer (preserve inline tags)
      buffer = buffer ? (buffer + ' ' + original) : original;
    } else {
      if (buffer) {
        // If this segment is also part of a broken sequence, merge it
        if (clean.length < 80 && !clean.includes('. ')) {
          buffer = buffer + ' ' + original;
        } else {
          merged.push(buffer);
          buffer = '';
          merged.push(original);
        }
      } else {
        merged.push(original);
      }
    }
  }
  if (buffer) merged.push(buffer);
  return merged.map(s => s ? `<p>${s}</p>` : '<br>').join('');
}

export const statusConfig = {
  '-2': { label: 'Declined', color: 'bg-red-100 text-red-700 border-red-300' },
  '-1': { label: 'Canceled', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  '0': { label: 'Draft', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  '1': { label: 'Assigned', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  '2': { label: 'For Submission', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  '3': { label: 'For Acceptance', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  '4': { label: 'Accepted', color: 'bg-green-100 text-green-700 border-green-300' },
  '5': { label: 'Job Booked', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  '6': { label: 'Invoiced', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

// Client-side image resize to reduce file size before upload
export function resizeImageFile(file, maxDim = 1200, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      // Only resize if larger than maxDim
      if (width <= maxDim && height <= maxDim) {
        // Still re-encode to reduce quality/size
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Canvas toBlob failed'));
            const resized = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
            resolve(resized);
          },
          'image/jpeg',
          quality
        );
        return;
      }
      // Scale down
      if (width > height) {
        height = Math.round((height / width) * maxDim);
        width = maxDim;
      } else {
        width = Math.round((width / height) * maxDim);
        height = maxDim;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Canvas toBlob failed'));
          const resized = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
          resolve(resized);
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

// Attachments Modal - Real file upload with object storage + image preview
